import React from 'react';
import { RoleSelectionProps } from '../types';
import controlPlaneRoles from '../../../data/control_plane_roles.json';

/**
 * Helper function to compare role IDs case-insensitively
 * Handles the casing difference between imported policies and data file
 */
const roleIdsMatch = (roleId1: string, roleId2: string): boolean => {
  return roleId1.toLowerCase() === roleId2.toLowerCase();
};

/**
 * RoleSelection component for managing Azure role assignments
 * 
 * This component provides an interface for selecting multiple Azure control plane roles,
 * featuring:
 * - Searchable role selection
 * - Multi-select capability
 * - Role removal
 * - Visual display of selected roles
 * 
 * @example
 * <RoleSelection
 *   roleDefinitionIds={selectedRoles}
 *   onUpdateRoles={handleRoleUpdate}
 * />
 */
export const RoleSelection: React.FC<RoleSelectionProps> = ({
  roleDefinitionIds,
  onUpdateRoles
}) => {
  // State for search functionality
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  
  // Reference for handling clicks outside dropdown
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  /**
   * Effect to handle clicks outside the dropdown
   * Closes the dropdown when clicking outside
   */
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    // Container for role selection, styled consistently
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Role Definition IDs</label>
      
      {/* Role search and selection dropdown */}
      <div className="relative" ref={dropdownRef}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          placeholder="Search and select roles..."
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
        />
        
        {/* Dropdown with filtered roles */}
        {isDropdownOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {controlPlaneRoles
              .filter(role => 
                role.roleName.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map(role => (
                <div key={role.roleId} className="text-gray-900 dark:text-white cursor-default select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={roleDefinitionIds.some(selectedId => roleIdsMatch(selectedId, role.roleId))}
                      onChange={(e) => {
                        const newRoleIds = e.target.checked
                          ? [...roleDefinitionIds, role.roleId]
                          : roleDefinitionIds.filter(id => !roleIdsMatch(id, role.roleId));
                        onUpdateRoles(newRoleIds);
                      }}
                      className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="font-normal block truncate">{role.roleName}</span>
                  </label>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Display selected roles with remove option */}
      <div className="mt-2 flex flex-wrap gap-2">
        {roleDefinitionIds.map(roleId => {
          const role = controlPlaneRoles.find(r => roleIdsMatch(r.roleId, roleId));
          return role ? (
            <div key={roleId} className="flex items-center justify-between bg-indigo-100 dark:bg-indigo-700 text-indigo-700 dark:text-indigo-100 text-sm font-medium px-2 py-1 rounded-full">
              <span>{role.roleName}</span>
              <button
                onClick={() => onUpdateRoles(roleDefinitionIds.filter(id => !roleIdsMatch(id, roleId)))}
                className="ml-2 text-indigo-500 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-100 focus:outline-none"

              >
                ×
              </button>
            </div>
          ) : null;
        })}
      </div>
    </div>
  );
}; 
